import { api } from "./client";
import { useAuthStore } from "@/store/auth";

// ─── Types ──────────────────────────────────────────────────

export interface Conversation {
  id: string;
  title: string;
  status: "idle" | "running" | "error";
  /** "inbox" = an automatic review of new email (see Today). */
  kind: "chat" | "inbox";
  created_at: string;
  updated_at: string;
}

export interface AssistantAction {
  id: string;
  kind: string;
  summary: string;
  payload: Record<string, unknown>;
  status: "pending" | "applied" | "dismissed" | "failed";
  result?: Record<string, unknown> | null;
  created_at?: string;
  resolved_at?: string | null;
}

export interface DocumentCard {
  id: string;
  kind: "pdf" | "research";
  title: string;
  company: string | null;
  summary: string | null;
  size_bytes: number;
  created_at: string;
}

/** One piece of a rendered turn, in order. */
export type DisplayPart =
  | { type: "text"; text: string; skill?: string; skill_title?: string }
  | { type: "tool"; label: string; error?: boolean }
  | { type: "proposal"; action: AssistantAction }
  | { type: "document"; document: DocumentCard };

export interface ChatMessage {
  id: string;
  seq: number;
  role: "user" | "assistant";
  display: DisplayPart[];
  created_at: string;
}

export interface ConversationDetail {
  conversation: Conversation;
  messages: ChatMessage[];
  actions: AssistantAction[];
}

export interface Skill {
  id: string;
  name: string;
  title: string;
  description: string;
  instructions: string;
  output: "chat" | "pdf" | "research";
  is_builtin: boolean;
  updated_at: string;
}

export type SkillInput = Pick<Skill, "name" | "title" | "description" | "instructions" | "output">;

export interface LibraryDocument extends DocumentCard {
  account_id: string | null;
  created_by: string | null;
}

export interface LibraryDocumentDetail extends LibraryDocument {
  content: Record<string, unknown>;
}

/** Events streamed while the assistant answers. */
export type StreamEvent =
  | { type: "start"; conversation_id: string }
  | { type: "delta"; text: string }
  | { type: "tool"; status: "start" | "done" | "error"; id: string; label: string }
  | { type: "proposal"; action: AssistantAction }
  | { type: "document"; document: DocumentCard }
  | { type: "error"; message: string }
  | { type: "done" };

// ─── Client ─────────────────────────────────────────────────

export const Assistant = {
  status: () =>
    api.get<{ configured: boolean; model: string }>("/assistant/status").then((r) => r.data),
  conversations: () => api.get<Conversation[]>("/assistant/conversations").then((r) => r.data),
  createConversation: () => api.post<Conversation>("/assistant/conversations").then((r) => r.data),
  conversation: (id: string) =>
    api.get<ConversationDetail>(`/assistant/conversations/${id}`).then((r) => r.data),
  deleteConversation: (id: string) => api.delete(`/assistant/conversations/${id}`),
  applyAction: (id: string) =>
    api.post<AssistantAction>(`/assistant/actions/${id}/apply`).then((r) => r.data),
  dismissAction: (id: string) =>
    api.post<AssistantAction>(`/assistant/actions/${id}/dismiss`).then((r) => r.data),

  /** Send a message and receive the reply as a stream of events. Uses fetch
   *  (not axios) so the body can be read incrementally. */
  async send(
    conversationId: string,
    body: { text: string; skill?: string | null },
    onEvent: (e: StreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = useAuthStore.getState().token;
    const res = await fetch(
      `${api.defaults.baseURL}/assistant/conversations/${conversationId}/messages`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
      },
    );
    if (res.status === 401) {
      useAuthStore.getState().logout();
      window.location.assign("/login");
      return;
    }
    if (!res.ok || !res.body) {
      let detail = `The assistant couldn't start (${res.status}).`;
      try {
        detail = ((await res.json()) as { detail?: string }).detail ?? detail;
      } catch {
        /* not JSON */
      }
      throw new Error(detail);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let cut: number;
      while ((cut = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, cut);
        buffer = buffer.slice(cut + 2);
        for (const line of frame.split("\n")) {
          if (line.startsWith("data: ")) onEvent(JSON.parse(line.slice(6)) as StreamEvent);
        }
      }
    }
  },
};

export const Skills = {
  list: () => api.get<Skill[]>("/skills").then((r) => r.data),
  create: (payload: SkillInput) => api.post<Skill>("/skills", payload).then((r) => r.data),
  importMarkdown: (markdown: string) =>
    api.post<Skill>("/skills/import", { markdown }).then((r) => r.data),
  update: (id: string, payload: Partial<SkillInput>) =>
    api.patch<Skill>(`/skills/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/skills/${id}`),
  exportMarkdown: (id: string) =>
    api.get<string>(`/skills/${id}/export`, { responseType: "text" }).then((r) => r.data),
};

export const Library = {
  list: (params?: { account_id?: string; kind?: "pdf" | "research" }) =>
    api.get<LibraryDocument[]>("/documents", { params }).then((r) => r.data),
  get: (id: string) => api.get<LibraryDocumentDetail>(`/documents/${id}`).then((r) => r.data),
  remove: (id: string) => api.delete(`/documents/${id}`),
  /** Fetch the PDF with the auth header and open it in a new tab. */
  async openPdf(id: string, download = false, filename = "document.pdf"): Promise<void> {
    const res = await api.get<Blob>(`/documents/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (download) {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    } else {
      window.open(url, "_blank", "noopener");
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

// ─── Inbox review ───────────────────────────────────────────

export interface InboxState {
  connected: boolean;
  review: Conversation | null;
  pending: (AssistantAction & { conversation_id: string })[];
}

export const Inbox = {
  state: () => api.get<InboxState>("/me/inbox").then((r) => r.data),
  /** Start a review. With `staleHours`, only if the last one is older than that. */
  sync: (staleHours?: number) =>
    api
      .post<InboxState>("/me/inbox/sync", null, {
        params: staleHours != null ? { stale_hours: staleHours } : undefined,
      })
      .then((r) => r.data),
};
